<template>
    <div id="completion-queue-container">
        <card title="Completion Queue">
            <b-container fluid>
                <b-row id="filter-row">
                    <b-col>
                        <SearchComponent v-model="storeFilter"
                                         label="Store"
                                         prepopulate
                                         clearable
                                         search-u-r-l="/Store"
                                         :params="{ active: true }" />
                    </b-col>
                    <b-col>
                        <SearchComponent v-model="programFilter"
                                         label="Program"
                                         clearable
                                         prepopulate
                                         search-u-r-l="/Program"
                                         :params="{ active: true }" />
                    </b-col>
                    <b-col class="d-flex align-items-end">
                        <b-button variant="dark" @click="clearFilters">
                            Clear Filters
                        </b-button>
                    </b-col>
                </b-row>
                <hr>
                <b-row>
                    <b-col>
                        <div v-if="isLoading" class="text-center my-4">
                            <b-spinner class="align-middle" variant="primary" />
                            <strong class="ml-2">Loading completion items...</strong>
                        </div>
                        <b-table v-else
                                 id="completion-queue-table"
                                 :items="completionItems"
                                 :fields="fields"
                                 show-empty
                                 sort-icon-left
                                 hover
                                 responsive
                                 empty-text="No pending completion items found.">
                            <template #cell(patient)="data">
                                <router-link :to="patientRoute(data.item)">
                                    {{ data.item.patientLastName }}, {{ data.item.patientFirstName }}
                                </router-link>
                            </template>
                            <template #cell(rxId)="data">
                                {{ data.item.storeId }}-{{ data.item.rxNumber }}-{{ data.item.rfNumber }}
                            </template>
                            <template #cell(parentOrderId)="data">
                                <router-link :to="orderRoute(data.item.parentOrderId)">
                                    #{{ data.item.parentOrderId }}
                                </router-link>
                            </template>
                            <template #cell(status)="data">
                                <b-badge :variant="statusVariant(data.item.status)">
                                    {{ statusLabel(data.item.status) }}
                                </b-badge>
                                <b-badge v-if="data.item.insuranceReviewRequired"
                                         variant="danger"
                                         class="ml-1"
                                         v-b-tooltip.hover.bottom="{ boundary: 'viewport' }"
                                         title="Insurance review required for this completion item">
                                    <i class="fas fa-exclamation-triangle" /> Insurance Review
                                </b-badge>
                            </template>
                            <template #cell(promisedDate)="data">
                                <span v-if="data.value">{{ data.value | formatDate('L') }}</span>
                                <span v-else class="text-muted">—</span>
                            </template>
                            <template #cell(created)="data">
                                {{ data.value | formatDate('L') }}
                            </template>
                            <template #cell(actions)="data">
                                <b-button size="sm"
                                          variant="primary"
                                          class="mr-1"
                                          v-b-tooltip.hover.bottom="{ boundary: 'viewport' }"
                                          title="Go to parent order"
                                          @click="navigateToOrder(data.item)">
                                    <i class="fas fa-external-link-alt" />
                                </b-button>
                                <b-button size="sm"
                                          variant="success"
                                          class="mr-1"
                                          v-b-tooltip.hover.bottom="{ boundary: 'viewport' }"
                                          title="Create completion order"
                                          :disabled="isCreatingOrder"
                                          @click="createOrderForItem(data.item)">
                                    <i class="fas fa-plus-circle" />
                                </b-button>
                                <b-button v-if="data.item.insuranceReviewRequired"
                                          size="sm"
                                          variant="warning"
                                          class="mr-1"
                                          v-b-tooltip.hover.bottom="{ boundary: 'viewport' }"
                                          title="Acknowledge insurance review"
                                          :disabled="isAcknowledging(data.item.id)"
                                          @click="acknowledgeInsuranceReview(data.item)">
                                    <i class="fas fa-check" />
                                </b-button>
                                <b-button size="sm"
                                          variant="danger"
                                          v-b-tooltip.hover.bottom="{ boundary: 'viewport' }"
                                          title="Cancel completion item"
                                          @click="showCancelModal(data.item)">
                                    <i class="fas fa-times" />
                                </b-button>
                            </template>
                        </b-table>
                    </b-col>
                </b-row>
            </b-container>
        </card>

        <b-modal id="cancel-modal"
                 title="Cancel Completion Item"
                 ok-variant="danger"
                 ok-title="Cancel Item"
                 :ok-disabled="!cancellationReason.trim() || isCancelling"
                 @ok="confirmCancel">
            <p>Are you sure you want to cancel this completion item?</p>
            <p v-if="itemToCancel">
                <strong>Patient:</strong> {{ itemToCancel.patientLastName }}, {{ itemToCancel.patientFirstName }}<br>
                <strong>Drug:</strong> {{ itemToCancel.drugName }}<br>
                <strong>Remaining Qty:</strong> {{ itemToCancel.remainingQty }}
            </p>
            <b-form-group label="Cancellation Reason:" label-for="cancellation-reason">
                <b-form-textarea id="cancellation-reason"
                                 v-model="cancellationReason"
                                 placeholder="Enter reason for cancellation..."
                                 rows="3"
                                 required />
            </b-form-group>
        </b-modal>

        <b-modal id="inventory-warning-modal"
                 title="Insufficient Inventory"
                 ok-variant="warning"
                 ok-title="Continue Anyway"
                 @ok="proceedToOrder">
            <p>
                <i class="fas fa-exclamation-triangle text-warning" />
                Current inventory may be insufficient to fill this completion item.
            </p>
            <p v-if="inventoryWarningItem">
                <strong>Drug:</strong> {{ inventoryWarningItem.drugName }}<br>
                <strong>Remaining Qty Needed:</strong> {{ inventoryWarningItem.remainingQty }}<br>
                <strong>Available On Hand:</strong> {{ inventoryOnHand }}
            </p>
        </b-modal>
    </div>
</template>

<script lang="ts">
import { Component, Vue, Watch } from "vue-property-decorator";
import SearchComponent from "@/components/SearchComponent.vue";
import CompletionItemService, {
    CompletionItemQueueView,
    CompletionItemStatus,
    completionItemStatusLabel,
    completionItemStatusVariant,
} from "@/services/CompletionItemService";
import { NotificationOptions } from "@/util/NotificationOptionsPresets";
import { Store } from "@/models/Store";
import { Program } from "@/models/Program";
import Axios from "axios";

@Component({
    name: "CompletionQueuePage",
    components: { SearchComponent },
})
export default class CompletionQueuePage extends Vue {
    private storeFilter: Store | null = new Store();
    private programFilter: Program | null = new Program();
    private completionItems: CompletionItemQueueView[] = [];
    private isLoading = false;

    private itemToCancel: CompletionItemQueueView | null = null;
    private cancellationReason = "";
    private isCancelling = false;

    private inventoryWarningItem: CompletionItemQueueView | null = null;
    private inventoryOnHand: number | null = null;
    private skipWatch = false;
    private isCreatingOrder = false;
    private pendingCallback: (() => Promise<void>) | null = null;

    // Per-item in-flight guard for the Acknowledge button. Array (not Set) for Vue 2 reactivity.
    private acknowledgingIds: number[] = [];

    private fields = [
        { key: "patient", label: "Patient", sortable: true },
        { key: "drugName", label: "Drug", sortable: true },
        { key: "rxId", label: "Rx ID", sortable: false },
        { key: "remainingQty", label: "Remaining Qty", sortable: true },
        { key: "parentOrderId", label: "Parent Order", sortable: true },
        { key: "storeName", label: "Store", sortable: true },
        { key: "promisedDate", label: "Promised Date", sortable: true },
        { key: "created", label: "Created", sortable: true },
        { key: "status", label: "Status", sortable: false },
        { key: "actions", label: "Actions", sortable: false },
    ];

    mounted() {
        this.loadCompletionItems();
    }

    @Watch("storeFilter")
    onStoreFilterChanged() {
        if (!this.skipWatch) this.loadCompletionItems();
    }

    @Watch("programFilter")
    onProgramFilterChanged() {
        if (!this.skipWatch) this.loadCompletionItems();
    }

    async loadCompletionItems() {
        this.isLoading = true;
        try {
            this.completionItems = await CompletionItemService.getPendingForQueue(
                this.storeFilter?.id ?? undefined,
                this.programFilter?.id ?? undefined,
            );
        } catch (err) {
            this.$notification(NotificationOptions.error(err));
            this.completionItems = [];
        } finally {
            this.isLoading = false;
        }
    }

    clearFilters() {
        this.skipWatch = true;
        this.storeFilter = new Store();
        this.programFilter = new Program();
        this.loadCompletionItems();
        this.$nextTick(() => { this.skipWatch = false; });
    }

    statusLabel(status: CompletionItemStatus): string {
        return completionItemStatusLabel(status);
    }

    statusVariant(status: CompletionItemStatus): string {
        return completionItemStatusVariant(status);
    }

    patientRoute(item: CompletionItemQueueView) {
        return { name: "PatientDashboard", params: { id: String(item.patientId) } };
    }

    orderRoute(orderId: number) {
        return { name: "OrderDetails", params: { orderId: String(orderId) } };
    }

    private async checkInventoryAndProceed(item: CompletionItemQueueView, onProceed: () => Promise<void>) {
        if (item.packageId && item.drugSource != null) {
            try {
                const response = await Axios.get<any[]>(
                    `/Drug/${item.packageId}/Inventory`,
                    { params: { drugSource: item.drugSource, fillingLocationsOnly: true } },
                );
                const totalOnHand = response.data.reduce(
                    (sum: number, inv: any) => sum + (inv.unitsOnHand ?? inv.quantity ?? 0), 0,
                );
                if (totalOnHand < item.remainingQty) {
                    this.inventoryWarningItem = item;
                    this.inventoryOnHand = totalOnHand;
                    this.pendingCallback = onProceed;
                    this.$bvModal.show("inventory-warning-modal");
                    return;
                }
            } catch (err) {
                // Block the action so a backend outage cannot bypass the insufficient-inventory guard.
                this.$notification(NotificationOptions.error(err));
                return;
            }
        }
        await onProceed();
    }

    async navigateToOrder(item: CompletionItemQueueView) {
        await this.checkInventoryAndProceed(item, async () => {
            this.$router.push(this.orderRoute(item.parentOrderId));
        });
    }

    async createOrderForItem(item: CompletionItemQueueView) {
        await this.checkInventoryAndProceed(item, () => this.executeCreateOrder(item));
    }

    async proceedToOrder() {
        this.inventoryWarningItem = null;
        this.inventoryOnHand = null;
        if (this.pendingCallback) {
            const callback = this.pendingCallback;
            this.pendingCallback = null;
            await callback();
        }
    }

    private async executeCreateOrder(item: CompletionItemQueueView) {
        this.isCreatingOrder = true;
        try {
            const response = await CompletionItemService.createOrder(item.id);
            this.$notification(NotificationOptions.success("Completion order created successfully."));
            this.$router.push(this.orderRoute(response.orderId));
        } catch (err) {
            this.$notification(NotificationOptions.error(err));
        } finally {
            this.isCreatingOrder = false;
        }
    }

    showCancelModal(item: CompletionItemQueueView) {
        this.itemToCancel = item;
        this.cancellationReason = "";
        this.$bvModal.show("cancel-modal");
    }

    async confirmCancel(bvEvent: any) {
        // Keep the modal open on validation/server failure so the user can amend the reason
        // and retry. We explicitly hide it only after a successful cancel.
        bvEvent.preventDefault();
        if (!this.itemToCancel || !this.cancellationReason.trim()) return;
        if (this.isCancelling) return;

        this.isCancelling = true;
        try {
            await CompletionItemService.cancel(this.itemToCancel.id, this.cancellationReason.trim());
            this.$notification(NotificationOptions.success("Completion item cancelled successfully."));
            this.itemToCancel = null;
            this.cancellationReason = "";
            this.$bvModal.hide("cancel-modal");
            await this.loadCompletionItems();
        } catch (err) {
            this.$notification(NotificationOptions.error(err));
        } finally {
            this.isCancelling = false;
        }
    }

    isAcknowledging(itemId: number): boolean {
        return this.acknowledgingIds.includes(itemId);
    }

    async acknowledgeInsuranceReview(item: CompletionItemQueueView) {
        if (this.isAcknowledging(item.id)) return;
        this.acknowledgingIds.push(item.id);
        try {
            await CompletionItemService.acknowledgeInsuranceReview(item.id);
            this.$notification(NotificationOptions.success("Insurance review acknowledged."));
            await this.loadCompletionItems();
        } catch (err) {
            this.$notification(NotificationOptions.error(err));
        } finally {
            this.acknowledgingIds = this.acknowledgingIds.filter(id => id !== item.id);
        }
    }
}
</script>

<style scoped>
#completion-queue-container {
    padding: 0;
}
</style>
